import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  PasswordResponseDto,
  PasswordCreateDto,
  PasswordUpdateDto,
  PasswordContentResponseDto,
} from '../../types/api';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../config/api';

export type PasswordItem = PasswordResponseDto;

export interface PasswordState {
  loading: boolean;
  items: PasswordItem[];
  error: string | null;
  selectedBoardId: string | null;
  selectedPasswordId: string | null;
  isAddModalOpen: boolean;
  isEditModalOpen: boolean;
  operationLoading: {
    list: boolean;
    get: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  errors: {
    list: string | null;
    get: string | null;
    create: string | null;
    update: string | null;
    delete: string | null;
  };
}

const initialOperationLoading = {
  list: false,
  get: false,
  create: false,
  update: false,
  delete: false,
};

const initialErrors = {
  list: null as string | null,
  get: null as string | null,
  create: null as string | null,
  update: null as string | null,
  delete: null as string | null,
};

const initialState: PasswordState = {
  loading: false,
  items: [],
  error: null,
  selectedBoardId: null,
  selectedPasswordId: null,
  isAddModalOpen: false,
  isEditModalOpen: false,
  operationLoading: { ...initialOperationLoading },
  errors: { ...initialErrors },
};

export const getPasswordsByVault = createAsyncThunk(
  'password/getPasswordsByVault',
  async (
    { vaultId, vaultMasterPassword }: { vaultId: string; vaultMasterPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const q = new URLSearchParams({ VaultMasterPassword: vaultMasterPassword });
      const items = await apiGet<PasswordItem[]>(
        `/api/Password/vault/${vaultId}?${q.toString()}`
      );
      return { vaultId, items };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şifreler yüklenirken hata oluştu');
    }
  }
);

export const getPassword = createAsyncThunk(
  'password/getPassword',
  async (id: string, { rejectWithValue }) => {
    try {
      return await apiGet<PasswordItem>(`/api/Password/${id}`);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şifre yüklenirken hata oluştu');
    }
  }
);

/** GET /api/Password/{id}/content – decrypted content only (Swagger: PasswordContentResponseDto). */
export const getPasswordContent = createAsyncThunk(
  'password/getPasswordContent',
  async (id: string, { rejectWithValue }) => {
    try {
      return await apiGet<PasswordContentResponseDto>(`/api/Password/${id}/content`);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şifre içeriği yüklenirken hata oluştu');
    }
  }
);

export const createPassword = createAsyncThunk(
  'password/createPassword',
  async (body: PasswordCreateDto, { rejectWithValue }) => {
    try {
      return await apiPost<PasswordItem>('/api/Password', body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şifre oluşturulurken hata oluştu');
    }
  }
);

export const updatePassword = createAsyncThunk(
  'password/updatePassword',
  async (
    { id, body }: { id: string; body: PasswordUpdateDto },
    { rejectWithValue }
  ) => {
    try {
      const item = await apiPatch<PasswordItem>(`/api/Password/${id}`, body);
      return { id, item };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şifre güncellenirken hata oluştu');
    }
  }
);

export const deletePassword = createAsyncThunk(
  'password/deletePassword',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiDelete(`/api/Password/${id}`);
      return id;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şifre silinirken hata oluştu');
    }
  }
);

const passwordSlice = createSlice({
  name: 'password',
  initialState,
  reducers: {
    setSelectedBoardId: (state, action: PayloadAction<string | null>) => {
      state.selectedBoardId = action.payload;
    },
    setSelectedPasswordId: (state, action: PayloadAction<string | null>) => {
      state.selectedPasswordId = action.payload;
    },
    setAddModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAddModalOpen = action.payload;
    },
    setEditModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isEditModalOpen = action.payload;
    },
    resetPasswordSelection: (state) => {
      state.selectedBoardId = null;
      state.selectedPasswordId = null;
      state.isAddModalOpen = false;
      state.isEditModalOpen = false;
    },
    clearPasswordError: (state) => {
      state.error = null;
      state.errors = { ...initialErrors };
    },
    clearPasswordItems: (state) => {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPasswordsByVault.pending, (state) => {
      state.operationLoading.list = true;
      state.error = null;
      state.errors.list = null;
    });
    builder.addCase(getPasswordsByVault.fulfilled, (state, action) => {
      state.operationLoading.list = false;
      state.items = Array.isArray(action.payload.items) ? action.payload.items : [];
      state.selectedBoardId = action.payload.vaultId;
      state.errors.list = null;
    });
    builder.addCase(getPasswordsByVault.rejected, (state, action) => {
      state.operationLoading.list = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Şifreler yüklenirken hata oluştu';
      state.error = msg;
      state.errors.list = msg;
    });

    builder.addCase(getPassword.pending, (state) => {
      state.operationLoading.get = true;
      state.errors.get = null;
    });
    builder.addCase(getPassword.fulfilled, (state, action) => {
      state.operationLoading.get = false;
      const item = action.payload;
      const idx = state.items.findIndex((i) => i.id === item.id);
      if (idx >= 0) state.items[idx] = item;
      else state.items.push(item);
      state.errors.get = null;
    });
    builder.addCase(getPassword.rejected, (state, action) => {
      state.operationLoading.get = false;
      state.errors.get = typeof action.payload === 'string' ? action.payload : 'Şifre yüklenirken hata oluştu';
    });

    builder.addCase(createPassword.pending, (state) => {
      state.operationLoading.create = true;
      state.errors.create = null;
    });
    builder.addCase(createPassword.fulfilled, (state, action) => {
      state.operationLoading.create = false;
      const item = action.payload;
      if (item?.id && !state.items.some((i) => i.id === item.id)) state.items.push(item);
      state.errors.create = null;
    });
    builder.addCase(createPassword.rejected, (state, action) => {
      state.operationLoading.create = false;
      state.errors.create = typeof action.payload === 'string' ? action.payload : 'Şifre oluşturulurken hata oluştu';
    });

    builder.addCase(updatePassword.pending, (state) => {
      state.operationLoading.update = true;
      state.errors.update = null;
    });
    builder.addCase(updatePassword.fulfilled, (state, action) => {
      state.operationLoading.update = false;
      const { id, item } = action.payload;
      const idx = state.items.findIndex((i) => i.id === id);
      if (idx >= 0 && item) state.items[idx] = item;
      state.errors.update = null;
    });
    builder.addCase(updatePassword.rejected, (state, action) => {
      state.operationLoading.update = false;
      state.errors.update = typeof action.payload === 'string' ? action.payload : 'Şifre güncellenirken hata oluştu';
    });

    builder.addCase(deletePassword.pending, (state) => {
      state.operationLoading.delete = true;
      state.errors.delete = null;
    });
    builder.addCase(deletePassword.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.errors.delete = null;
    });
    builder.addCase(deletePassword.rejected, (state, action) => {
      state.operationLoading.delete = false;
      state.errors.delete = typeof action.payload === 'string' ? action.payload : 'Şifre silinirken hata oluştu';
    });
  },
});

export const {
  setSelectedBoardId,
  setSelectedPasswordId,
  setAddModalOpen,
  setEditModalOpen,
  resetPasswordSelection,
  clearPasswordError,
  clearPasswordItems,
} = passwordSlice.actions;
export default passwordSlice.reducer;
